import { ChangeDetectionStrategy, Component, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  ChevronRight,
  ChevronUp,
  LogOut,
  LucideAngularModule,
  LucideIconData,
  Settings,
  Shield,
  UserRound,
  X,
} from 'lucide-angular';
import { filter } from 'rxjs';

import { AuthService } from '../../core/auth/auth.service';
import { BrandingService } from '../../core/branding/branding.service';
import { OfficeMarkComponent } from '../../shared/components/office-mark/office-mark.component';
import { LayoutService } from '../layout.service';
import { NAV_SECTIONS, NavItem, filterNavSections } from '../navigation/navigation.config';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, LucideAngularModule, OfficeMarkComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="sidebar">
      <!-- Marca -->
      <div class="sidebar-brand">
        <app-office-mark />
        <span class="brand-name" [title]="branding.displayName()">
          <strong>{{ branding.displayName() }}</strong>
          @if (branding.hasOffice()) {
            <small>Escritório</small>
          }
        </span>
        <button type="button" class="sidebar-close" aria-label="Fechar menu" (click)="layout.closeNav()">
          <lucide-icon [img]="closeIcon" [size]="18" aria-hidden="true"></lucide-icon>
        </button>
      </div>

      <!-- Navegação -->
      <nav class="sidebar-nav" aria-label="Navegação principal">
        @for (section of sections(); track section.label) {
          <div class="nav-section">
            <span class="nav-section-label">{{ section.label }}</span>

            @for (item of section.items; track item.label) {
              @if (item.children) {
                <button
                  type="button"
                  class="nav-item"
                  [class.section-active]="isSectionActive(item)"
                  [attr.aria-expanded]="isGroupOpen(item)"
                  (click)="toggleGroup(item)">
                  <lucide-icon class="nav-icon" [img]="item.icon" [size]="16" aria-hidden="true"></lucide-icon>
                  <span class="nav-label">{{ item.label }}</span>
                  <lucide-icon
                    class="nav-caret"
                    [class.open]="isGroupOpen(item)"
                    [img]="caretIcon"
                    [size]="14"
                    aria-hidden="true"></lucide-icon>
                </button>

                @if (isGroupOpen(item)) {
                  <div class="nav-sublist" role="group" [attr.aria-label]="item.label">
                    @for (child of item.children; track child.route) {
                      <a
                        class="nav-subitem"
                        [routerLink]="child.route"
                        routerLinkActive="active"
                        [routerLinkActiveOptions]="{ exact: child.exact ?? false }"
                        ariaCurrentWhenActive="page">
                        {{ child.label }}
                      </a>
                    }
                  </div>
                }
              } @else {
                <a
                  class="nav-item"
                  [routerLink]="item.route"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
                  ariaCurrentWhenActive="page">
                  <lucide-icon class="nav-icon" [img]="item.icon" [size]="16" aria-hidden="true"></lucide-icon>
                  <span class="nav-label">{{ item.label }}</span>
                </a>
              }
            }
          </div>
        }
      </nav>

      <!-- Usuário -->
      <div class="user-footer">
        @if (userMenuOpen()) {
          <div class="user-menu" role="menu" aria-label="Menu do usuário">
            <button type="button" class="user-menu-item" role="menuitem" [disabled]="!canOpenProfile()" (click)="openProfile()">
              <lucide-icon [img]="userIcon" [size]="15" aria-hidden="true"></lucide-icon>
              <span>Meu perfil</span>
            </button>
            <button type="button" class="user-menu-item" role="menuitem" [disabled]="!canOpenSettings()" (click)="openSettings()">
              <lucide-icon [img]="settingsIcon" [size]="15" aria-hidden="true"></lucide-icon>
              <span>Configurações</span>
            </button>
            <button type="button" class="user-menu-item" role="menuitem" (click)="openSecurity()">
              <lucide-icon [img]="shieldIcon" [size]="15" aria-hidden="true"></lucide-icon>
              <span>Segurança</span>
            </button>
            <div class="user-menu-divider" aria-hidden="true"></div>
            <button type="button" class="user-menu-item user-menu-item-danger" role="menuitem" (click)="logout()">
              <lucide-icon [img]="logoutIcon" [size]="15" aria-hidden="true"></lucide-icon>
              <span>Sair da conta</span>
            </button>
          </div>
        }

        <button
          type="button"
          class="user-trigger"
          [class.open]="userMenuOpen()"
          aria-label="Menu do usuário"
          aria-haspopup="menu"
          [attr.aria-expanded]="userMenuOpen()"
          (click)="toggleUserMenu()">
          <span class="user-avatar" aria-hidden="true">{{ userInitials() }}</span>
          <span class="user-info">
            <strong>{{ userName() }}</strong>
            <small>{{ roleLabel() }}</small>
          </span>
          <lucide-icon class="user-chevron" [img]="chevronIcon" [size]="14" aria-hidden="true"></lucide-icon>
        </button>
      </div>
    </div>
  `,
  styles: `
    :host { display: block; height: 100%; }

    .sidebar {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: var(--color-sidebar-bg);
      color: var(--color-sidebar-text);
      border-right: 1px solid var(--color-sidebar-border);
    }

    /* ── Marca ───────────────────────────────────────────── */
    .sidebar-brand {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      min-height: var(--topbar-height);
      padding: 0.5rem 1rem;
      flex-shrink: 0;
    }

    .brand-name {
      flex: 1;
      min-width: 0;
      display: grid;
      line-height: 1.15;
    }

    .brand-name small {
      font-size: 0.6875rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--color-sidebar-text);
    }

    /* Nome longo quebra em até duas linhas em vez de ser cortado. */
    .brand-name strong {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      overflow-wrap: anywhere;
      line-height: 1.15;
      font-family: var(--font-display);
      font-size: 1rem;
      font-weight: 600;
      color: var(--color-sidebar-text-active);
      letter-spacing: -0.01em;
    }

    .sidebar-close {
      width: 36px;
      height: 36px;
      display: grid;
      place-items: center;
      border: 1px solid var(--color-sidebar-border);
      border-radius: var(--radius-xs);
      background: transparent;
      color: var(--color-sidebar-text);
      cursor: pointer;
    }

    .sidebar-close:hover { background: var(--color-sidebar-hover); color: var(--color-sidebar-text-active); }

    /* ── Navegação ───────────────────────────────────────── */
    .sidebar-nav {
      flex: 1;
      overflow-y: auto;
      padding: 0.25rem 0.75rem 1rem;
      scrollbar-width: thin;
    }

    .nav-section { display: flex; flex-direction: column; gap: 2px; }

    .nav-section-label {
      padding: 1rem 0.65rem 0.35rem;
      font-size: 0.65625rem;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--color-sidebar-muted);
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      width: 100%;
      min-height: 38px;
      padding: 0.45rem 0.65rem;
      border: 0;
      border-radius: var(--radius-xs);
      background: transparent;
      color: var(--color-sidebar-text);
      font: inherit;
      font-size: 0.875rem;
      text-align: left;
      text-decoration: none;
      cursor: pointer;
      transition: background var(--transition-fast), color var(--transition-fast);
      -webkit-tap-highlight-color: transparent;
    }

    .nav-item:hover { background: var(--color-sidebar-hover); color: var(--color-sidebar-text-active); }

    .nav-item:focus-visible,
    .nav-subitem:focus-visible,
    .user-trigger:focus-visible,
    .sidebar-close:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 1px;
    }

    .nav-item.active {
      background: var(--color-sidebar-active);
      color: var(--color-sidebar-text-active);
      font-weight: 600;
      box-shadow: inset 2px 0 0 var(--color-sidebar-active-border);
    }

    .nav-item.section-active { color: var(--color-sidebar-text-active); font-weight: 600; }

    .nav-icon { flex-shrink: 0; opacity: 0.85; }
    .nav-item.active .nav-icon,
    .nav-item.section-active .nav-icon { opacity: 1; color: var(--color-sidebar-active-border); }

    .nav-label { flex: 1; min-width: 0; }

    .nav-caret { flex-shrink: 0; opacity: 0.6; transition: transform var(--transition-fast); }
    .nav-caret.open { transform: rotate(90deg); }

    .nav-sublist {
      display: grid;
      gap: 1px;
      margin: 2px 0 4px 1.45rem;
      padding-left: 0.6rem;
      border-left: 1px solid var(--color-sidebar-border);
    }

    .nav-subitem {
      display: flex;
      align-items: center;
      min-height: 34px;
      padding: 0.35rem 0.6rem;
      border-radius: var(--radius-xs);
      color: var(--color-sidebar-text);
      font-size: 0.84375rem;
      text-decoration: none;
      transition: background var(--transition-fast), color var(--transition-fast);
    }

    .nav-subitem:hover { background: var(--color-sidebar-hover); color: var(--color-sidebar-text-active); }

    .nav-subitem.active {
      background: var(--color-sidebar-active);
      color: var(--color-sidebar-text-active);
      font-weight: 600;
      box-shadow: inset 2px 0 0 var(--color-sidebar-active-border);
    }

    /* ── Usuário ─────────────────────────────────────────── */
    .user-footer {
      position: relative;
      flex-shrink: 0;
      padding: 0.6rem 0.75rem calc(0.6rem + env(safe-area-inset-bottom, 0px));
      border-top: 1px solid var(--color-sidebar-border);
    }

    .user-trigger {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 0.65rem;
      min-height: 46px;
      padding: 0.4rem 0.5rem;
      border: 1px solid transparent;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-sidebar-text);
      text-align: left;
      cursor: pointer;
    }

    .user-trigger:hover,
    .user-trigger.open { background: var(--color-sidebar-hover); }

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--color-ink);
      border: 1px solid var(--color-sidebar-border);
      color: var(--color-on-ink);
      display: grid;
      place-items: center;
      font-size: 0.75rem;
      font-weight: 600;
      flex-shrink: 0;
    }

    .user-info { flex: 1; min-width: 0; display: grid; gap: 1px; }

    .user-info strong,
    .user-info small {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.25;
    }

    .user-info strong { font-size: 0.84375rem; font-weight: 600; color: var(--color-sidebar-text-active); }
    .user-info small { font-size: 0.71875rem; color: var(--color-sidebar-muted); }

    .user-chevron { flex-shrink: 0; color: var(--color-sidebar-muted); transition: transform var(--transition-base); }
    .user-trigger.open .user-chevron { transform: rotate(180deg); }

    .user-menu {
      position: absolute;
      left: 0.75rem;
      right: 0.75rem;
      bottom: calc(100% + 0.35rem);
      z-index: 10;
      display: grid;
      gap: 1px;
      padding: 0.35rem;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-lg);
      animation: menu-in 130ms ease;
    }

    .user-menu-item {
      display: flex;
      align-items: center;
      gap: 0.55rem;
      width: 100%;
      min-height: 38px;
      padding: 0.45rem 0.6rem;
      border: 0;
      border-radius: var(--radius-xs);
      background: transparent;
      color: var(--color-text-primary);
      font: inherit;
      font-size: 0.84375rem;
      text-align: left;
      cursor: pointer;
    }

    .user-menu-item:hover:not(:disabled) { background: var(--color-soft-surface); }
    .user-menu-item:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
    .user-menu-item:disabled { opacity: 0.4; cursor: not-allowed; }
    .user-menu-item-danger { color: var(--color-danger-fg); }
    .user-menu-item-danger:hover:not(:disabled) { background: var(--color-danger-bg); }
    .user-menu-divider { height: 1px; margin: 0.2rem 0; background: var(--color-border); }

    @keyframes menu-in {
      from { transform: translateY(4px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    @media (min-width: 1024px) {
      .sidebar-close { display: none; }
    }

    @media (prefers-reduced-motion: reduce) {
      .user-menu { animation: none; }
    }
  `,
})
export class SidebarComponent {
  protected readonly auth = inject(AuthService);
  protected readonly layout = inject(LayoutService);
  private readonly router = inject(Router);
  protected readonly branding = inject(BrandingService);

  readonly userIcon: LucideIconData = UserRound;
  readonly settingsIcon: LucideIconData = Settings;
  readonly logoutIcon: LucideIconData = LogOut;
  readonly chevronIcon: LucideIconData = ChevronUp;
  readonly shieldIcon: LucideIconData = Shield;
  readonly caretIcon: LucideIconData = ChevronRight;
  readonly closeIcon: LucideIconData = X;

  protected readonly sections = computed(() =>
    filterNavSections(NAV_SECTIONS, (permission) => this.auth.hasPermission(permission)),
  );

  private readonly currentUrl = signal(this.router.url);
  /** Grupos abertos manualmente; o grupo da página atual abre sozinho. */
  private readonly openGroups = signal<ReadonlySet<string>>(new Set());
  private readonly closedGroups = signal<ReadonlySet<string>>(new Set());

  protected readonly userMenuOpen = signal(false);
  protected readonly userName = computed(() => this.auth.session()?.user?.name ?? 'Usuário');
  protected readonly userEmail = computed(() => this.auth.session()?.user?.email ?? '');
  protected readonly userPublicId = computed(() => this.auth.session()?.user?.public_id?.trim() ?? '');
  protected readonly userInitials = computed(() => this.buildInitials(this.userName(), this.userEmail()));
  protected readonly roleLabel = computed(() => this.resolveRoleLabel());
  protected readonly profileRoute = computed(() => this.resolveProfileRoute());
  protected readonly settingsRoute = computed(() => this.resolveSettingsRoute());
  protected readonly canOpenProfile = computed(() => Boolean(this.profileRoute()));
  protected readonly canOpenSettings = computed(() => Boolean(this.settingsRoute()));

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.userMenuOpen.set(false);
      });
  }

  protected isSectionActive(item: NavItem): boolean {
    const url = this.currentUrl();
    if (item.sectionPrefix && url.startsWith(item.sectionPrefix)) return true;
    return (item.children ?? []).some((child) => url.startsWith(child.route));
  }

  protected isGroupOpen(item: NavItem): boolean {
    if (this.closedGroups().has(item.label)) return false;
    return this.openGroups().has(item.label) || this.isSectionActive(item);
  }

  protected toggleGroup(item: NavItem): void {
    const open = this.isGroupOpen(item);
    this.openGroups.update((set) => this.withToggled(set, item.label, !open));
    this.closedGroups.update((set) => this.withToggled(set, item.label, open));
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.userMenuOpen.set(false);
    this.layout.closeNav();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('app-sidebar .user-footer')) {
      this.userMenuOpen.set(false);
    }
  }

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
  }

  protected openProfile(): void {
    const route = this.profileRoute();
    if (!route) return;
    this.userMenuOpen.set(false);
    void this.router.navigateByUrl(route);
  }

  protected openSettings(): void {
    const route = this.settingsRoute();
    if (!route) return;
    this.userMenuOpen.set(false);
    void this.router.navigateByUrl(route);
  }

  protected openSecurity(): void {
    this.userMenuOpen.set(false);
    void this.router.navigateByUrl('/app/account/security');
  }

  protected logout(): void {
    this.userMenuOpen.set(false);
    this.layout.closeNav();
    this.auth.logout();
  }

  private withToggled(set: ReadonlySet<string>, key: string, include: boolean): ReadonlySet<string> {
    const next = new Set(set);
    if (include) next.add(key);
    else next.delete(key);
    return next;
  }

  private resolveRoleLabel(): string {
    const user = this.auth.session()?.user;
    if (!user) return 'Perfil';
    if (user.is_admin) return 'Administrador';
    const roleName = this.extractPrimaryRoleName(user.roles);
    return roleName ? this.formatRoleName(roleName) : 'Perfil';
  }

  private resolveProfileRoute(): string | null {
    const publicId = this.userPublicId();
    if (!publicId || !this.auth.hasPermission('users.update')) return null;
    return `/app/users/${publicId}/edit`;
  }

  private resolveSettingsRoute(): string | null {
    if (this.auth.hasPermission('settings.view')) return '/app/settings';
    if (this.auth.hasPermission('integrations.view')) return '/app/settings/integrations/asaas';
    if (this.auth.hasPermission('integrations.google_calendar.view')) return '/app/settings/integrations/google-calendar';
    if (this.auth.hasPermission('webhooks.view')) return '/app/settings/webhooks';
    if (this.auth.hasPermission('office_profile.view')) return '/app/settings/office-profile';
    if (this.auth.hasPermission('email_deliveries.view')) return '/app/settings/email-deliveries';
    return null;
  }

  private extractPrimaryRoleName(roles: string[] | { name: string }[] | undefined): string | null {
    if (!Array.isArray(roles) || roles.length === 0) return null;
    for (const role of roles) {
      if (typeof role === 'string' && role.trim()) return role;
      if (role && typeof role === 'object' && 'name' in role) {
        const v = (role as { name?: unknown }).name;
        if (typeof v === 'string' && v.trim()) return v;
      }
    }
    return null;
  }

  private buildInitials(name: string, email: string): string {
    const src = (name.trim() || email.trim() || 'LX').split(/\s+/).filter(Boolean);
    return src.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || 'LX';
  }

  private formatRoleName(value: string): string {
    const n = value.replace(/[_-]+/g, ' ').trim();
    if (!n) return 'Perfil';
    return n.split(/\s+/).map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ');
  }
}
